import { useCallback, useEffect, useRef, useState } from 'react';
import { Prompt, useLocation } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { IconArrowUp, IconPlus, IconSettings, IconX } from '@tabler/icons-react';
import { history } from 'src/utils/history';
import IntentService, { errorMessage } from 'src/services/IntentService';
import { clearIntentHandoff, readIntentHandoff } from 'src/utils/intentHandoff';

import EconomicsCard from './EconomicsCard';
import PackageCheckout from './PackageCheckout';
import { LeadsPanel, ProcessingPanel, ResultsPanel } from './WorkPanels';
import { BillingPanel, GiftPanel, OrderCard, ReferralPanel } from './CommercePanels';
import WorkspaceShell, { projectSections } from './WorkspaceShell';
import ProjectContext from './ProjectContext';
import ProjectOverview from './ProjectOverview';
import WorkspaceHelp, { publicPage } from './WorkspaceHelp';
import { resolveWorkspace } from './projectSelection';

import type { Capabilities, Catalog, Conversation, Order, Quote, Workspace } from 'src/services/IntentService';
import './intent-workspace.css';

const tabs = projectSections;
const sourceStates = { connected: 'Подключён', no_recent_data: 'Нет свежих данных', not_connected: 'Не подключён' };
const streamStates: Record<string, string> = {
    active: 'Поиск работает',
    pending: 'Нужен пакет',
    paused: 'Поиск на паузе',
    ended: 'Лиды закончились',
};

export default function IntentWorkspace() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const requestedProject = params.get('project');
    const view = params.get('view') || (readIntentHandoff('gift') ? 'gift' : 'chat');
    const globalView = ['projects', 'help'].includes(view);
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [draft, setDraft] = useState<Workspace | null>(null);
    const [catalog, setCatalog] = useState<Catalog | null>(null);
    const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
    const [wallet, setWallet] = useState<string | null>(null);
    const [available, setAvailable] = useState(false);
    const [dialogs, setDialogs] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [retryKey, setRetryKey] = useState(0);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [conversation, setConversation] = useState<Conversation | null>(null);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [message, setMessage] = useState('');
    const [query, setQuery] = useState('');
    const [quote, setQuote] = useState<Quote | null>(null);
    const [order, setOrder] = useState<Order | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const loadedDialogAction = useRef('');
    const sendingProject = useRef<number | null>(null);
    const dialogSelection = useRef(0);
    const active = resolveWorkspace(workspaces, requestedProject);
    const missingProject = requestedProject !== null && !active;
    const activeProjectRef = useRef<number | null>(null);
    activeProjectRef.current = active?.id ?? null;
    const dirty = Boolean(
        active &&
            draft &&
            JSON.stringify({ name: active.name, p: active.profile, e: active.economics }) !==
                JSON.stringify({ name: draft.name, p: draft.profile, e: draft.economics })
    );
    const navigate = (nextView: string, id = active?.id) =>
        history.push(
            '/workspace?' + new URLSearchParams({ ...(id ? { project: String(id) } : {}), view: nextView }).toString()
        );
    const refresh = useCallback(async () => {
        const result = await IntentService.list();
        setWorkspaces(result.workspaces);
        setWallet(result.wallet);
        setDialogs(result.dialogs);
        return result.workspaces;
    }, []);
    useEffect(() => {
        let live = true;
        const init = async () => {
            setLoading(true);
            setError('');
            try {
                const [cap, cat] = await Promise.all([IntentService.capabilities(), IntentService.catalog()]);
                if (!live) return;
                setCapabilities(cap);
                setCatalog(cat);
                const token = readIntentHandoff('draft');
                if (token) {
                    try {
                        const w = await IntentService.claimDraft(token);
                        clearIntentHandoff('draft');
                        if (live) history.replace('/workspace?project=' + w.id);
                    } catch (e) {
                        if (live) setError(errorMessage(e));
                    }
                }
                const referral = readIntentHandoff('referral');
                if (referral) {
                    try {
                        await IntentService.claimReferral(referral);
                        clearIntentHandoff('referral');
                    } catch (e) {
                        if (live) setError(errorMessage(e));
                    }
                }
                if (live) {
                    await refresh();
                    if (live) setAvailable(true);
                }
                if (live && readIntentHandoff('gift')) history.replace('/workspace?view=gift');
                const orderId = new URLSearchParams(window.location.search).get('order');
                if (orderId && /^[a-f0-9-]{36}$/i.test(orderId)) {
                    const current = await IntentService.order(orderId);
                    if (live) setOrder(current);
                }
            } catch (e) {
                if (live) {
                    setAvailable(false);
                    setWallet(null);
                    setError(errorMessage(e));
                }
            } finally {
                if (live) setLoading(false);
            }
        };
        void init();
        return () => {
            live = false;
        };
    }, [refresh, retryKey]);
    useEffect(() => {
        setDraft(active);
        setQuote(null);
        setMessage('');
        setQuery('');
        setSearchOpen(false);
        if (sendingProject.current === active?.id) return;
        const selection = ++dialogSelection.current;
        setConversation(null);
        setConversations([]);
        if (!active) return;
        let live = true;
        IntentService.conversations(active.id)
            .then(async (list) => {
                if (!live) return;
                setConversations(list);
                const requested = new URLSearchParams(window.location.search).get('conversation');
                const first = list.find((c) => c.id === requested) || list[0];
                if (first) {
                    const data = await IntentService.conversation(first.id);
                    if (live && selection === dialogSelection.current) setConversation(data);
                }
            })
            .catch((e) => {
                if (live) setError(errorMessage(e));
            });
        return () => {
            live = false;
        };
    }, [active?.id, active?.revision]);
    useEffect(() => {
        if (params.get('new') === '1') setCreateOpen(true);
    }, [location.search]);
    useEffect(() => {
        if (params.get('search') === '1') setSearchOpen(true);
        if (params.get('help') === '1') setHelpOpen(true);
        const action = params.get('new_dialog');
        if (!action || loadedDialogAction.current === action || loading || !available || missingProject) return;
        loadedDialogAction.current = action;
        if (!active) {
            setCreateOpen(true);
            return;
        }
        let live = true;
        const selection = ++dialogSelection.current;
        IntentService.newConversation(active.id)
            .then((c) => {
                if (live && selection === dialogSelection.current) {
                    setConversation(c);
                    setConversations((old) => [c, ...old]);
                    setMessage('');
                }
            })
            .catch((e) => {
                if (live) setError(errorMessage(e));
            });
        return () => {
            live = false;
        };
    }, [location.search, loading, available, active?.id]);
    useEffect(() => {
        if (!order || !['pending', 'submitting'].includes(order.state)) return;
        let live = true;
        const timer = setInterval(() => {
            IntentService.order(order.id)
                .then((next) => {
                    if (live) {
                        setOrder(next);
                        if (next.state === 'paid') void refresh();
                    }
                })
                .catch(() => {});
        }, 10000);
        return () => {
            live = false;
            clearInterval(timer);
        };
    }, [order?.id, order?.state, refresh]);
    const replaceWorkspace = (next: Workspace) => {
        setWorkspaces((old) =>
            old.some((w) => w.id === next.id) ? old.map((w) => (w.id === next.id ? next : w)) : [next, ...old]
        );
    };
    const create = async () => {
        if (!available || !newName.trim()) return;
        setBusy(true);
        setError('');
        try {
            const w = await IntentService.create(newName.trim());
            replaceWorkspace(w);
            setCreateOpen(false);
            setNewName('');
            navigate('chat', w.id);
            setProfileOpen(true);
            window.dispatchEvent(new Event('intent-workspaces-updated'));
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    const save = async () => {
        if (!available || !draft || draft.id !== active?.id) return;
        setBusy(true);
        setError('');
        try {
            const w = await IntentService.save(draft);
            replaceWorkspace(w);
            if (activeProjectRef.current === w.id) setProfileOpen(false);
            window.dispatchEvent(new Event('intent-workspaces-updated'));
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    const readWebsite = async () => {
        if (!available || !draft?.profile.website) return;
        setBusy(true);
        setError('');
        const website = draft.profile.website;
        const projectId = draft.id;
        try {
            const result = await IntentService.website(draft.id, website);
            setDraft((w) =>
                w && w.id === projectId && w.profile.website === website
                    ? { ...w, profile: { ...w.profile, website_notes: result.notes } }
                    : w
            );
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    const attachNotes = async (file?: File) => {
        if (!file) return;
        const projectId = draft?.id;
        if (!/\.(txt|md)$/i.test(file.name) || file.size > 64000) {
            setError('Добавьте TXT или MD размером до 64 КБ.');
            return;
        }
        try {
            const text = await file.text();
            if (text.length > 12000) {
                setError('В файле больше 12 000 символов. Оставьте описание бизнеса, предложения и условия.');
                return;
            }
            setDraft((w) => (w && w.id === projectId ? { ...w, profile: { ...w.profile, knowledge_notes: text } } : w));
        } catch {
            setError('Не удалось прочитать файл. Добавьте текст вручную.');
        }
    };
    const startConversation = async () => {
        if (!available || busy || missingProject) return;
        if (!active) {
            setCreateOpen(true);
            return;
        }
        setBusy(true);
        setError('');
        const projectId = active.id;
        const selection = ++dialogSelection.current;
        try {
            const c = await IntentService.newConversation(projectId);
            if (activeProjectRef.current !== projectId || selection !== dialogSelection.current) return;
            setConversation(c);
            setConversations((old) => [c, ...old]);
            navigate('chat');
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    const send = async () => {
        const text = message.trim();
        if (!available || missingProject || !text || busy) return;
        setBusy(true);
        setError('');
        ++dialogSelection.current;
        try {
            let w = active;
            if (!w) {
                w = await IntentService.create(text.slice(0, 70));
                sendingProject.current = w.id;
                activeProjectRef.current = w.id;
                replaceWorkspace(w);
                navigate('chat', w.id);
                window.dispatchEvent(new Event('intent-workspaces-updated'));
            }
            const c = conversation || (await IntentService.newConversation(w.id));
            if (activeProjectRef.current !== w.id) return;
            setConversation(c);
            const next = await IntentService.message(c.id, text);
            if (activeProjectRef.current !== w.id) return;
            setConversation(next);
            setMessage('');
            const list = await IntentService.conversations(w.id);
            if (activeProjectRef.current !== w.id) return;
            setConversations(list);
            if (next.suggestion) {
                const suggested = next.suggestion;
                const projectId = w.id;
                setDraft((current) =>
                    current && current.id === projectId
                        ? {
                              ...current,
                              profile: {
                                  ...current.profile,
                                  description: current.profile.description || suggested.niche || '',
                                  region: current.profile.region || suggested.region || '',
                                  category_id: current.profile.category_id || suggested.matched_category_id || null,
                                  criteria: current.profile.criteria || (suggested.criteria || []).join('\n'),
                              },
                          }
                        : current
                );
            }
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            sendingProject.current = null;
            setBusy(false);
        }
    };
    const selectConversation = async (id: string) => {
        if (!active || busy) return;
        const projectId = active.id;
        const selection = ++dialogSelection.current;
        try {
            const next = await IntentService.conversation(id);
            if (activeProjectRef.current !== projectId || dialogSelection.current !== selection) return;
            setConversation(next);
            setSearchOpen(false);
            navigate('chat', projectId);
        } catch (e) {
            if (activeProjectRef.current === projectId && dialogSelection.current === selection)
                setError(errorMessage(e));
        }
    };
    const textField = (
        field: 'description' | 'website' | 'region' | 'criteria' | 'exclusions' | 'recommendation_url',
        label: string,
        multiline = false
    ) => (
        <TextField
            fullWidth
            label={label}
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            value={draft?.profile[field] || ''}
            inputProps={{ maxLength: multiline ? 2000 : 1000 }}
            onChange={(e) => setDraft((w) => (w ? { ...w, profile: { ...w.profile, [field]: e.target.value } } : w))}
        />
    );
    const onOrder = (o: Order) => {
        setOrder(o);
        if (o.state === 'paid') void refresh();
    };
    return (
        <WorkspaceShell
            projects={workspaces}
            active={globalView ? null : active}
            view={view}
            loading={loading}
            available={available}
            wallet={wallet}
            navigate={navigate}
            onNew={startConversation}
            onCreate={() => setCreateOpen(true)}
            onSearch={() => setSearchOpen(true)}
            onSettings={() => setProfileOpen(true)}
            context={
                globalView ? (
                    <Stack
                        className="project-context-content"
                        spacing={2}
                    >
                        <Typography
                            component="h2"
                            fontWeight={700}
                        >
                            Рабочее пространство
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {available
                                ? `${workspaces.length} потока · ${
                                      workspaces.filter((project) => project.streams.some((stream) => stream.is_active))
                                          .length
                                  } с активным поиском`
                                : 'Данные временно недоступны'}
                        </Typography>
                        <Typography variant="body2">
                            Выберите поток, чтобы увидеть его лиды, экономику и воронку.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setCreateOpen(true)}
                            disabled={!available}
                        >
                            Создать поток
                        </Button>
                        <Button
                            component="a"
                            href={publicPage('/methodology/index.html')}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Как мы отбираем лиды ↗
                        </Button>
                    </Stack>
                ) : (
                    <ProjectContext
                        workspace={active}
                        available={available}
                        loading={loading}
                        launch={() => navigate('launch')}
                    />
                )
            }
        >
            <Prompt
                when={dirty && !busy}
                message="В потоке есть несохранённые изменения. Перейти без сохранения?"
            />
            <Box className="intent-workspace">
                <Box className="intent-workspace-header">
                    <div>
                        <Typography
                            component="h1"
                            variant="h4"
                        >
                            {view === 'projects'
                                ? 'Потоки лидов'
                                : view === 'help'
                                ? 'Помощь и материалы'
                                : active?.name ||
                                  (['billing', 'gift', 'referral'].includes(view) ? 'Аккаунт' : 'Новый запуск')}
                        </Typography>
                        <Typography
                            color="text.secondary"
                            variant="body2"
                            mt={0.5}
                        >
                            {view === 'projects'
                                ? 'Бизнес, диалоги и результаты — каждый поток отдельно'
                                : view === 'help'
                                ? 'Ответы и следующий шаг'
                                : active?.streams.length
                                ? active.streams.map((s) => streamStates[s.status] || 'Проверяется').join(' · ')
                                : 'Поиск покупательского спроса в открытых источниках'}
                        </Typography>
                    </div>
                    <Stack
                        direction="row"
                        gap={1}
                    >
                        {active && !globalView && (
                            <IconButton
                                aria-label="Настройки потока"
                                onClick={() => setProfileOpen(true)}
                            >
                                <IconSettings size={20} />
                            </IconButton>
                        )}
                        <Button
                            startIcon={<IconPlus size={18} />}
                            onClick={() => setCreateOpen(true)}
                            disabled={loading || !available}
                        >
                            Поток
                        </Button>
                    </Stack>
                </Box>
                {error && (available || loading) && (
                    <Alert
                        severity="error"
                        onClose={() => setError('')}
                        sx={{ my: 2 }}
                    >
                        {error}
                    </Alert>
                )}
                {!loading && !available && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                    >
                        Не удалось загрузить данные кабинета. {error || 'Проверьте соединение и повторите попытку.'}{' '}
                        Действующие потоки доступны в текущем кабинете.
                        <Box mt={1}>
                            <Button
                                size="small"
                                onClick={() => setRetryKey((value) => value + 1)}
                            >
                                Повторить загрузку
                            </Button>
                        </Box>
                    </Alert>
                )}
                {available && missingProject && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                    >
                        Поток не найден или недоступен. Выберите поток в меню — данные другого потока не подставляются.
                    </Alert>
                )}
                {loading ? (
                    <Typography
                        sx={{ py: 6 }}
                        color="text.secondary"
                    >
                        Загружаем рабочее пространство…
                    </Typography>
                ) : (
                    <>
                        {!!workspaces.length && !['projects', 'help'].includes(view) && (
                            <TextField
                                select
                                size="small"
                                label="Поток"
                                value={active?.id || ''}
                                onChange={(e) => navigate(view, Number(e.target.value))}
                                sx={{ minWidth: 240, maxWidth: '100%', mb: 2, display: { xs: 'flex', md: 'none' } }}
                            >
                                {workspaces.map((w) => (
                                    <MenuItem
                                        key={w.id}
                                        value={w.id}
                                    >
                                        {w.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        {active && tabs.some((t) => t.id === view) && (
                            <Tabs
                                value={view}
                                onChange={(_, next) => navigate(next)}
                                variant="scrollable"
                                allowScrollButtonsMobile
                                aria-label="Разделы потока"
                                sx={{ mb: 3, display: { xs: 'flex', md: 'none' } }}
                            >
                                {tabs.map((t) => (
                                    <Tab
                                        key={t.id}
                                        value={t.id}
                                        label={t.title}
                                    />
                                ))}
                            </Tabs>
                        )}
                        {order && (
                            <Box mb={3}>
                                <OrderCard
                                    order={order}
                                    onChange={onOrder}
                                />
                            </Box>
                        )}
                        <Box
                            className={
                                view !== 'chat' && (active || ['billing', 'gift', 'referral'].includes(view))
                                    ? `intent-surface intent-surface--${view}`
                                    : undefined
                            }
                        >
                            {view === 'projects' ? (
                                <ProjectOverview
                                    projects={workspaces}
                                    available={available}
                                    open={navigate}
                                    create={() => setCreateOpen(true)}
                                />
                            ) : view === 'help' ? (
                                <WorkspaceHelp />
                            ) : view === 'billing' ? (
                                <BillingPanel
                                    key={active?.id || 'account'}
                                    workspace={active}
                                    wallet={wallet}
                                    onGift={() => navigate('gift')}
                                    onReferral={() => navigate('referral')}
                                />
                            ) : view === 'gift' ? (
                                <GiftPanel
                                    capabilities={capabilities}
                                    onOrder={onOrder}
                                    onActivated={() => {
                                        void refresh();
                                        navigate('billing');
                                    }}
                                />
                            ) : view === 'referral' ? (
                                <ReferralPanel />
                            ) : active && (view === 'leads' || view === 'funnel') ? (
                                <LeadsPanel
                                    key={active.id + view}
                                    workspace={active}
                                    mode={view === 'funnel' ? 'funnel' : 'list'}
                                    onLaunch={() => navigate('launch')}
                                />
                            ) : active && view === 'processing' ? (
                                <ProcessingPanel
                                    key={active.id}
                                    workspace={active}
                                    dialogs={dialogs}
                                />
                            ) : active && view === 'results' ? (
                                <ResultsPanel
                                    key={active.id}
                                    workspace={active}
                                />
                            ) : (
                                <Box className="intent-chat-work">
                                    <Box
                                        className="intent-conversation"
                                        sx={{ display: view === 'launch' && active ? 'none' : undefined }}
                                    >
                                        {!conversation?.messages?.length && (
                                            <Box className="intent-chat-empty">
                                                <Typography
                                                    variant="h4"
                                                    component="h2"
                                                >
                                                    Расскажите о бизнесе
                                                </Typography>
                                                <Typography
                                                    color="text.secondary"
                                                    mt={1}
                                                >
                                                    Что продаёте и кому? Можно добавить ссылку на сайт.
                                                </Typography>
                                                <Box className="intent-source-pills">
                                                    <span>Чаты</span>
                                                    <span>Сообщества</span>
                                                    <span>Комментарии</span>
                                                    <span>Сайты и форумы</span>
                                                </Box>
                                            </Box>
                                        )}
                                        {searchOpen && !!conversations.length && (
                                            <Box className="intent-dialog-picker">
                                                <TextField
                                                    size="small"
                                                    label="Поиск диалогов"
                                                    value={query}
                                                    onChange={(e) => setQuery(e.target.value)}
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={startConversation}
                                                    disabled={busy}
                                                >
                                                    Новый диалог
                                                </Button>
                                                <Box>
                                                    {conversations
                                                        .filter(
                                                            (c) =>
                                                                !query ||
                                                                c.title
                                                                    .toLocaleLowerCase()
                                                                    .includes(query.toLocaleLowerCase())
                                                        )
                                                        .map((c) => (
                                                            <Button
                                                                key={c.id}
                                                                size="small"
                                                                color={
                                                                    c.id === conversation?.id ? 'primary' : 'secondary'
                                                                }
                                                                disabled={busy}
                                                                onClick={() => selectConversation(c.id)}
                                                            >
                                                                {c.title}
                                                            </Button>
                                                        ))}
                                                </Box>
                                            </Box>
                                        )}
                                        <Box
                                            aria-live="polite"
                                            className="intent-transcript"
                                        >
                                            {conversation?.messages?.map((m, i) => (
                                                <div
                                                    key={i}
                                                    className={'intent-message intent-message--' + m.role}
                                                >
                                                    <span>{m.role === 'user' ? 'Вы' : 'Лидсмания'}</span>
                                                    <p>{m.content}</p>
                                                </div>
                                            ))}
                                        </Box>
                                        {busy && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Выполняем запрос…
                                            </Typography>
                                        )}
                                        {!dismissed && !error && (conversation?.messages?.length || 0) >= 4 && (
                                            <Box className="intent-context-banner">
                                                <div>
                                                    <strong>Пригласите знакомого</strong>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {Number(capabilities?.referral_reward) > 0
                                                            ? 'Бонус после его первого платного запуска'
                                                            : 'Поделитесь сервисом поиска клиентов'}
                                                    </Typography>
                                                </div>
                                                <Button onClick={() => navigate('referral')}>Пригласить</Button>
                                                <IconButton
                                                    aria-label="Скрыть приглашение"
                                                    onClick={() => setDismissed(true)}
                                                >
                                                    <IconX size={16} />
                                                </IconButton>
                                            </Box>
                                        )}
                                        <Box
                                            component="form"
                                            className="intent-composer"
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                void send();
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                multiline
                                                maxRows={7}
                                                placeholder="Расскажите о бизнесе или добавьте ссылку на сайт"
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                inputProps={{ maxLength: 600, 'aria-label': 'Сообщение о бизнесе' }}
                                                onKeyDown={(e) => {
                                                    if (
                                                        e.key === 'Enter' &&
                                                        !e.shiftKey &&
                                                        !(e.nativeEvent as KeyboardEvent).isComposing
                                                    ) {
                                                        e.preventDefault();
                                                        void send();
                                                    }
                                                }}
                                                sx={{ '& fieldset': { border: 0 } }}
                                            />
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                px={1}
                                                pb={1}
                                            >
                                                <Button
                                                    size="small"
                                                    disabled={!available || missingProject}
                                                    onClick={() =>
                                                        active ? setProfileOpen(true) : setCreateOpen(true)
                                                    }
                                                >
                                                    О бизнесе
                                                </Button>
                                                <IconButton
                                                    className="intent-send"
                                                    type="submit"
                                                    disabled={!available || missingProject || busy || !message.trim()}
                                                    aria-label="Отправить сообщение"
                                                >
                                                    <IconArrowUp size={20} />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            textAlign="center"
                                            mt={1}
                                        >
                                            ИИ помогает настроить запуск. Покупка и изменение правил — после
                                            подтверждения.
                                        </Typography>
                                    </Box>
                                    {draft && view === 'chat' && (
                                        <Box className="intent-next-step">
                                            <div>
                                                <Typography
                                                    component="h2"
                                                    fontWeight={600}
                                                >
                                                    Следующий шаг
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {dirty
                                                        ? 'Проверьте и сохраните предложенные изменения профиля.'
                                                        : active?.streams.length
                                                        ? 'Проверьте новые лиды или уточните экономику потока.'
                                                        : 'Проверьте профиль, рассчитайте ценность лида и выберите пакет.'}
                                                </Typography>
                                            </div>
                                            <Stack
                                                direction="row"
                                                gap={1}
                                                flexWrap="wrap"
                                            >
                                                {dirty ? (
                                                    <Button
                                                        variant="contained"
                                                        onClick={save}
                                                        disabled={busy}
                                                    >
                                                        Сохранить профиль
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        onClick={() =>
                                                            navigate(active?.streams.length ? 'leads' : 'launch')
                                                        }
                                                    >
                                                        {active?.streams.length ? 'Открыть лиды' : 'Рассчитать запуск'}
                                                    </Button>
                                                )}
                                                <Button onClick={() => setProfileOpen(true)}>Проверить профиль</Button>
                                            </Stack>
                                        </Box>
                                    )}
                                    {draft && view === 'launch' && (
                                        <Box className="intent-launch-plan">
                                            <EconomicsCard
                                                value={draft.economics}
                                                onChange={(value) =>
                                                    setDraft((w) => (w ? { ...w, economics: value } : w))
                                                }
                                                quote={quote}
                                            />
                                            {dirty && (
                                                <Button
                                                    variant="outlined"
                                                    sx={{ my: 2 }}
                                                    onClick={save}
                                                    disabled={busy}
                                                >
                                                    Сохранить профиль и расчёт
                                                </Button>
                                            )}
                                            <PackageCheckout
                                                key={draft.id}
                                                workspace={draft}
                                                catalog={catalog}
                                                capabilities={capabilities}
                                                wallet={wallet}
                                                dirty={dirty}
                                                onQuote={setQuote}
                                                onOrder={onOrder}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                        {active?.streams.length && !['projects', 'help'].includes(view) ? (
                            <Stack
                                direction="row"
                                spacing={1}
                                mt={4}
                            >
                                <Button
                                    size="small"
                                    onClick={async () => {
                                        setBusy(true);
                                        try {
                                            replaceWorkspace(
                                                await IntentService.search(
                                                    active.id,
                                                    active.streams.some((s) => s.is_active) ? 'pause' : 'resume'
                                                )
                                            );
                                        } catch (e) {
                                            setError(errorMessage(e));
                                        } finally {
                                            setBusy(false);
                                        }
                                    }}
                                    disabled={busy}
                                >
                                    {active.streams.some((s) => s.is_active)
                                        ? 'Приостановить поиск'
                                        : 'Продолжить поиск'}
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => navigate('billing')}
                                >
                                    Остатки и оплата
                                </Button>
                            </Stack>
                        ) : null}
                        <Box className="intent-help">
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Нужна помощь с запуском?
                            </Typography>
                            <Button
                                component="a"
                                href={publicPage('/consultation/index.html')}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                Написать специалисту
                            </Button>
                        </Box>
                    </>
                )}
                <Dialog
                    open={createOpen}
                    onClose={() => !busy && setCreateOpen(false)}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>Новый поток</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label="Название бизнеса или направления"
                            value={newName}
                            inputProps={{ maxLength: 120 }}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') void create();
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
                        <Button
                            variant="contained"
                            disabled={busy || !newName.trim()}
                            onClick={create}
                        >
                            Создать
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={searchOpen}
                    onClose={() => setSearchOpen(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>Поиск диалогов потока</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label="Название диалога"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <Stack>
                            {conversations
                                .filter((c) => c.title.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
                                .map((c) => (
                                    <Button
                                        key={c.id}
                                        disabled={busy}
                                        onClick={() => selectConversation(c.id)}
                                    >
                                        {c.title}
                                    </Button>
                                ))}
                        </Stack>
                        {!conversations.length && (
                            <Typography color="text.secondary">В этом потоке пока нет диалогов</Typography>
                        )}
                    </DialogContent>
                </Dialog>
                <Dialog
                    open={helpOpen}
                    onClose={() => setHelpOpen(false)}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>Помощь с запуском</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Поможем с настройкой ниши, обработкой и оплатой. Пароль и данные карты отправлять не нужно.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setHelpOpen(false)}>Закрыть</Button>
                        <Button
                            component="a"
                            href="mailto:support@leadsmania.ru"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Написать специалисту
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={profileOpen && !!draft}
                    onClose={() => !busy && setProfileOpen(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>Настройки потока</DialogTitle>
                    <DialogContent>
                        {error && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                            >
                                {error}
                            </Alert>
                        )}
                        {draft && (
                            <Stack
                                spacing={2.5}
                                pt={1}
                            >
                                <TextField
                                    label="Название"
                                    value={draft.name}
                                    inputProps={{ maxLength: 120 }}
                                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                />
                                {textField('description', 'Что продаёте и кому', true)}
                                {textField('website', 'Сайт — необязательно')}
                                {textField('region', 'Регион')}
                                <Button
                                    variant="outlined"
                                    onClick={readWebsite}
                                    disabled={busy || !draft.profile.website}
                                >
                                    Прочитать страницу сайта
                                </Button>
                                {draft.profile.website_notes && (
                                    <>
                                        <Alert severity="info">
                                            Прочитана одна страница, не весь сайт. Проверьте текст перед сохранением.
                                        </Alert>
                                        <TextField
                                            label="Информация со страницы"
                                            multiline
                                            minRows={3}
                                            maxRows={8}
                                            value={draft.profile.website_notes}
                                            inputProps={{ maxLength: 12000 }}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    profile: { ...draft.profile, website_notes: e.target.value },
                                                })
                                            }
                                        />
                                    </>
                                )}
                                <Button
                                    component="label"
                                    variant="outlined"
                                >
                                    Прикрепить описание · TXT, MD
                                    <input
                                        hidden
                                        type="file"
                                        accept=".txt,.md,text/plain,text/markdown"
                                        onChange={(e) => {
                                            void attachNotes(e.target.files?.[0]);
                                            e.target.value = '';
                                        }}
                                    />
                                </Button>
                                {draft.profile.knowledge_notes !== undefined && (
                                    <TextField
                                        label="Описание из файла"
                                        multiline
                                        minRows={3}
                                        maxRows={8}
                                        value={draft.profile.knowledge_notes}
                                        inputProps={{ maxLength: 12000 }}
                                        onChange={(e) =>
                                            setDraft({
                                                ...draft,
                                                profile: { ...draft.profile, knowledge_notes: e.target.value },
                                            })
                                        }
                                    />
                                )}
                                <TextField
                                    select
                                    label="Категория спроса"
                                    value={draft.profile.category_id || ''}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            profile: { ...draft.profile, category_id: Number(e.target.value) },
                                        })
                                    }
                                >
                                    <MenuItem value="">Выберите категорию</MenuItem>
                                    {catalog?.categories.map((c) => (
                                        <MenuItem
                                            key={c.id}
                                            value={c.id}
                                            disabled={!c.available}
                                        >
                                            {c.name}
                                            {c.available ? '' : ' · недоступна'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {textField('criteria', 'Какие запросы подходят', true)}
                                {textField('exclusions', 'Что исключить', true)}
                                <TextField
                                    select
                                    label="Цель обработки"
                                    value={draft.profile.processing_goal || 'manual'}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            profile: { ...draft.profile, processing_goal: e.target.value },
                                        })
                                    }
                                >
                                    <MenuItem value="manual">Обрабатываю сам</MenuItem>
                                    <MenuItem value="qualification">Квалификация</MenuItem>
                                    <MenuItem value="recommendation">Полезная рекомендация</MenuItem>
                                </TextField>
                                {draft.profile.processing_goal === 'recommendation' &&
                                    textField('recommendation_url', 'Страница или канал для рекомендации')}
                                <Typography variant="h6">Открытые источники</Typography>
                                {catalog?.sources.map((s) => (
                                    <Stack
                                        key={s.id}
                                        direction="row"
                                        justifyContent="space-between"
                                        gap={2}
                                    >
                                        <div>
                                            <Typography>{s.name}</Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {s.kind}
                                            </Typography>
                                        </div>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {sourceStates[s.status]}
                                        </Typography>
                                    </Stack>
                                ))}
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Статус показывает фактическое подключение, не гарантирует спрос по вашей нише.
                                    Правила применяются к новым выдачам после сохранения. Материалы потока используются
                                    в диалоге настройки; базу знаний ИИ-менеджера подтвердите отдельно.
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                >
                                    <Button
                                        component="a"
                                        href="/knowledge-base"
                                    >
                                        База знаний
                                    </Button>
                                    <Button
                                        component="a"
                                        href="/integrations"
                                    >
                                        CRM
                                    </Button>
                                    <Button
                                        component="a"
                                        href="/notifications"
                                    >
                                        Уведомления
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setProfileOpen(false)}
                            disabled={busy}
                        >
                            Закрыть
                        </Button>
                        <Button
                            variant="contained"
                            onClick={save}
                            disabled={busy || !draft?.name.trim()}
                        >
                            Сохранить правила
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </WorkspaceShell>
    );
}
